import {
  approximateLocation,
  editJobSchema,
  formatPostalCode,
  type JobLocation,
  WANTED_STARTS,
  type EditJobInput,
  type WantedStart,
} from '@hemmafixa/shared';
import {
  Button,
  FormField,
  InlinePanel,
  InlinePanelFooter,
  Input,
  LocationPicker,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
} from '@hemmafixa/ui';
import type { UseMutationResult } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useI18n } from '@/i18n/context';
import { ApiError } from '@/lib/api';
import { env } from '@/lib/env';
import { useCategoryLabel, useCategoryOptions } from './catalogue';

interface EditableJob {
  title: string;
  description: string;
  categorySlug: string;
  postalCode: string;
  wantedStart: WantedStart;
  location: JobLocation | null;
}

type FieldErrors = Partial<Record<keyof EditableJob, string>>;

/**
 * Edits a job's fields while it is in review: the client on their job page, admin in the review queue. Opens in
 * place of the job's description. The API checks the category and postal code against the catalogue; its field
 * errors are shown on the fields.
 */
export function JobEditForm({
  job,
  description,
  mutation,
  onClose,
}: {
  job: EditableJob;
  description: string;
  mutation: UseMutationResult<unknown, Error, EditJobInput>;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const e = t.jobEdit;
  const options = useCategoryOptions();
  const categoryLabel = useCategoryLabel();
  const [form, setForm] = useState({ ...job, postalCode: formatPostalCode(job.postalCode) });
  const [errors, setErrors] = useState<FieldErrors>({});
  const set = <K extends keyof EditableJob>(key: K, value: EditableJob[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  // An old job may use a slug the catalogue no longer lists; keep it selectable
  const categories = options.some((option) => option.slug === job.categorySlug)
    ? options
    : [{ slug: job.categorySlug, label: categoryLabel(job.categorySlug), isMain: true }, ...options];

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const parsed = editJobSchema.safeParse(form);
    if (!parsed.success) {
      const fields = parsed.error.flatten().fieldErrors;
      setErrors(Object.fromEntries(Object.keys(fields).map((key) => [key, e.invalidField])));
      return;
    }
    setErrors({});
    mutation.mutate(parsed.data, {
      onSuccess: () => {
        onClose();
        toast.success(e.savedToast);
      },
      onError: (error) => {
        if (error instanceof ApiError && error.code === 'VALIDATION_FAILED' && error.fields?.length) {
          setErrors(
            Object.fromEntries(
              error.fields.map((field) => [
                field.path,
                field.path === 'postalCode'
                  ? e.unknownPostalCode
                  : field.path === 'categorySlug'
                    ? e.unknownCategory
                    : e.invalidField,
              ]),
            ),
          );
          return;
        }
        toast.error(error instanceof ApiError && error.code === 'CONFLICT' ? e.conflict : e.saveFailed);
      },
    });
  };

  return (
    <InlinePanel
      title={e.title}
      description={description}
      onClose={onClose}
      onSubmit={submit}
      closeLabel={t.common.cancel}
    >
      <FormField label={e.fieldTitle} error={errors.title} required>
        <Input maxLength={100} value={form.title} onChange={(event) => set('title', event.target.value)} />
      </FormField>
      <FormField label={e.fieldDescription} error={errors.description} required>
        <Textarea
          rows={6}
          maxLength={4000}
          value={form.description}
          onChange={(event) => set('description', event.target.value)}
        />
      </FormField>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label={t.job.category} error={errors.categorySlug} required>
          <Select value={form.categorySlug} onValueChange={(value) => set('categorySlug', value)}>
            <SelectTrigger aria-label={t.job.category}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {categories.map((option) => (
                <SelectItem
                  key={option.slug}
                  value={option.slug}
                  className={option.isMain ? 'font-semibold' : 'pl-12'}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
        <FormField label={e.fieldPostalCode} error={errors.postalCode} required>
          <Input
            inputMode="numeric"
            autoComplete="postal-code"
            maxLength={6}
            value={form.postalCode}
            onChange={(event) => set('postalCode', event.target.value)}
          />
        </FormField>
      </div>
      {/* A fieldset, since the map is not one input a label can point at */}
      <fieldset className="grid gap-1.5">
        <legend className="mb-1.5 text-sm font-medium">{t.job.mapField}</legend>
        <LocationPicker
          value={form.location}
          onChange={(location) => set('location', location)}
          apiKey={env.googleMapsApiKey}
          initialCenter={approximateLocation(form.postalCode)}
          labels={{
            hint: t.job.mapHint,
            useMyLocation: t.job.useMyLocation,
            clear: t.job.clearPin,
            unavailable: t.job.mapUnavailable,
          }}
        />
      </fieldset>
      <FormField label={t.job.wantedStart} required>
        <Select value={form.wantedStart} onValueChange={(value) => set('wantedStart', value as WantedStart)}>
          <SelectTrigger aria-label={t.job.wantedStart}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WANTED_STARTS.map((value) => (
              <SelectItem key={value} value={value}>
                {t.wantedStart[value]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FormField>
      <InlinePanelFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          {t.common.cancel}
        </Button>
        <Button type="submit" loading={mutation.isPending}>
          {t.common.save}
        </Button>
      </InlinePanelFooter>
    </InlinePanel>
  );
}
